import { formatRFC3339 } from 'date-fns';
import _ from 'lodash';
import { Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import * as uuid from 'uuid';

import { Inject, Injectable } from '@angular/core';

import { SN_API_URL, SN_SECONDARY_API_URL } from '@lib/api';
import { SnWidget } from '@lib/api/entities';
import * as ArcGIS from '@lib/arcgis';
import { FsTimeService } from '@lib/common/services';
import { FsLprCamerasService } from '@lib/vehicle-intelligence/api';

import { SnLayersAPI } from './layers.service';
import { SnOperationalViewsAPI } from './operational-views.service';
import { SnUsersAPI } from './users.service';

@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  public widget: SnWidget;
  public loadedData: any[] = [];
  public avlOptions: any[] = [];
  public isLoading: boolean = false;
  public isCreatedtimestampAvailable: boolean;

  private readonly _destroy$ = new Subject<void>();
  private readonly _loadedData$ = new Subject<any[]>();

  get loadedData$(): Observable<any[]> {
    return this._loadedData$.asObservable();
  }

  constructor(
    @Inject(SN_API_URL) private readonly _apiUrl: string,
    @Inject(SN_SECONDARY_API_URL) private readonly _secondApiUrl: string,
    private readonly _layersAPI: SnLayersAPI,
    private readonly _lprCamerasService: FsLprCamerasService,
    private readonly _usersAPI: SnUsersAPI,
    private readonly _timeService: FsTimeService,
    private readonly _widgetsService: SnOperationalViewsAPI,
  ) {}

  public storeData(data: any): void {
    this.loadedData.push(data);
    this._loadedData$.next(this.loadedData);
  }

  public checkIfLoadedData(widget: SnWidget, refreshLayers: boolean): any {
    if (refreshLayers) {
      this.loadedData = [];
    }
    this.widget = widget;
    const index = this.loadedData.findIndex((data) => {
      return data.layer.widgetId === widget.id;
    });

    if (index !== -1) {
      this._loadedData$.next(this.loadedData);
    } else {
      this._loadFromWidget(widget);
    }
  }

  public updateWidgetMeta(viewId: string, widgetId: string, layers: any): Observable<any> {
    return this._widgetsService.updateViewWidget(viewId, widgetId, {
      meta: {
        layers,
      },
    });
  }

  private _loadFromWidget(widget: SnWidget): void {
    widget.meta.layers.forEach((layer) => {
      switch (layer.mapDataType) {
        case 'cad':
          this._loadCad(layer, widget);
          break;
        case 'avl':
          this._loadAvl(layer, widget);
          break;
        case 'lpr':
          this._lprLayers(layer, widget);
          break;
        case 'users':
          this._usersLayer(layer, widget);
          break;
      }
    });

    this.isLoading = false;
  }

  private _loadCad(layer: any, widget: SnWidget): void {
    this._layersAPI
      .getLayerHits(layer.layer.id, layer.layer.params)
      .pipe(
        map((response) => ({
          ...response,
          results: response.results.map((res) => ({
            lat: res.geometry.coordinates[1],
            lon: res.geometry.coordinates[0],
            createdAt: new Date(res.createdTimestamp),
          })),
        })),
      )
      .subscribe((res) => {
        this.loadedData.push({
          layer: {
            id: uuid.v4(),
            layers: layer,
            loadedData: res.results,
            widgetId: widget.id,
          },
        });
        this._loadedData$.next(this.loadedData);
      });
  }

  private _loadAvl(layer: any, widget: SnWidget): void {
    const featureLayer = new ArcGIS.FeatureLayer(layer.layer.definitionUrl);
    const query = new ArcGIS.Query(layer.layer.meta.filters);
    const isAPI =
      layer.layer.definitionUrl.startsWith(this._apiUrl) ||
      layer.layer.definitionUrl.startsWith(this._secondApiUrl);
    if (isAPI) {
      const token = localStorage.getItem('JWT');
      query.headers({
        Authorization: `JWT ${token}`,
      });
      query.fetch((url: string, options: any) => {
        return fetch(url.replace('query?', 'query/?'), options);
      });

      const aYearAgo = new Date();
      aYearAgo.setFullYear(aYearAgo.getFullYear() - 1);
      query.params({
        date_from: formatRFC3339(aYearAgo),
        page_size: 'all',
      });
    }
    featureLayer
      .queryFeatures(query, layer)
      .pipe(takeUntil(this._destroy$))
      .subscribe((response) => {
        if (response.features?.length) {
          this.avlOptions = response.features.map((feature) => {
            return {
              value: feature.id,
              label: feature.id,
            };
          });
          this.avlOptions.sort((a, b) => {
            if (a.label > b.label) {
              return 1;
            }

            if (a.label < b.label) {
              return -1;
            }

            return 0;
          });
        }

        this._fetchAvlData(layer, widget);
      });
  }

  private _fetchAvlData(layer: any, widget: SnWidget): void {
    const filters = layer.layer.params;
    const avlSearch = [];
    this.avlOptions.forEach((avl) => {
      avlSearch.push(avl.value);
    });
    filters.pageMapClustered = false;
    filters.pageSize = 'all';
    this._layersAPI
      .getLayerHits(layer.layer.id, {
        created_timestamp__gte: this.isCreatedtimestampAvailable
          ? layer.created_timestamp__gte || layer.createdTimestamp_Gte
          : null,
        created_timestamp__lte: this.isCreatedtimestampAvailable
          ? layer.created_timestamp__lte || layer.createdTimestamp_Lte
          : null,
        updated_timestamp__gte: !this.isCreatedtimestampAvailable
          ? layer.created_timestamp__gte || layer.createdTimestamp_Gte
          : null,
        updated_timestamp__lte: !this.isCreatedtimestampAvailable
          ? layer.created_timestamp__lte || layer.createdTimestamp_Lte
          : null,
        attributes__id__in: avlSearch.join(', '),
        pageSize: 'all',
        page_map_clustered: false,
      })
      .pipe(takeUntil(this._destroy$))
      .subscribe((res) => {
        const formmattedResults = res.results.map((avl) => {
          return {
            lat: avl.geometry.coordinates[1],
            lon: avl.geometry.coordinates[0],
            unit: avl.name,
          };
        });

        const points = _(formmattedResults)
          .groupBy('unit')
          .map((value, name) => ({ formmattedResults: value, unit: name }))
          .value();

        this.loadedData.push({
          layer: {
            id: uuid.v4(),
            layers: layer,
            loadedData: points,
            widgetId: widget.id,
          },
        });
        this._loadedData$.next(this.loadedData);
      });
  }

  private _lprLayers(layer: any, widget: SnWidget): void {
    const types = [];
    layer.lprType.forEach((lpr) => {
      types.push(lpr);
    });

    const lastEventFrom = layer.createdTimestamp_Gte;
    const lastEventTo = layer.createdTimestamp_Lte;

    const params: any = {
      last_event_from: lastEventFrom,
      last_event_to: lastEventTo,
    };

    if (types.length) {
      params.provider = types;
    }

    this._lprCamerasService
      .getCameraList(params)
      .pipe(takeUntil(this._destroy$))
      .subscribe((res) => {
        const formmattedResults = res.results.map((read) => {
          return {
            lat: read.latitude,
            lon: read.longitude,
            lprType: read.provider,
            lastObservedDate: read.lastEvent,
          };
        });
        this.loadedData.push({
          layer: {
            id: uuid.v4(),
            layers: layer,
            loadedData: formmattedResults,
            widgetId: widget.id,
          },
        });
        this._loadedData$.next(this.loadedData);
      });
  }

  private _usersLayer(layer: any, widget: SnWidget): void {
    const createdTimestampGte = new Date(layer.createdTimestamp_Gte);
    const createdTimestampLte = new Date(layer.createdTimestamp_Lte);
    const userSearch = layer.users_search?.join(', ');
    const params = {
      user_id__in: userSearch,
      timestamp__gte: this._timeService.convertToUnixTimestampMilliseconds(createdTimestampGte),
      timestamp__lte: this._timeService.convertToUnixTimestampMilliseconds(createdTimestampLte),
      pageSize: 'all',
    };

    this._usersAPI
      .breadcrumbs(params)
      .pipe(takeUntil(this._destroy$))
      .subscribe((res) => {
        if (res.data.length) {
          const formmattedResults = res.data.map((user) => {
            return {
              lat: user.geometry.coordinates[1],
              lon: user.geometry.coordinates[0],
              user: user.name,
            };
          });

          const points = _(formmattedResults)
            .groupBy('user')
            .map((value, name) => ({ formmattedResults: value, name }))
            .value();
          this.loadedData.push({
            layer: {
              id: uuid.v4(),
              layers: layer,
              loadedData: points,
              widgetId: widget.id,
            },
          });
          this._loadedData$.next(this.loadedData);
        }
      });
  }
}
